import { ConfigStateService, LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfigurationSettingsDto } from 'src/core/models/configuration-setting/configuration-settings.dto';
import { ConfigurationSettingsService } from 'src/core/services/configuration-settings/configuration-settings.service';
import { StringValidator } from 'src/core/validators/shared/string.validator';

@Component({
    selector: 'ca-amazon-s3-integration-settings',
    templateUrl: './amazon-s3-integration-settings.component.html',
    styleUrls: ['./amazon-s3-integration-settings.component.scss'],
    standalone: false
})
export class AmazonS3IntegrationSettingsComponent implements OnInit {
  form: FormGroup;

  private readonly prefix = 'AmazonS3Integration';
  private readonly sourceName = `${this.prefix}.SourceName`;
  private readonly organizationId = `${this.prefix}.OrganizationId`;
  private readonly clientId = `${this.prefix}.ClientId`;
  private readonly clientSecret = `${this.prefix}.ClientSecret`;
  private readonly region = `${this.prefix}.Region`;
  private readonly bucketName = `${this.prefix}.BucketName`;
  private readonly outputBucketName = `${this.prefix}.OutputBucketName`;
  private readonly templatePath = `${this.prefix}.TemplatePath`;
  private readonly outputTemplatePath = `${this.prefix}.OutputTemplatePath`;
  private readonly kinesisRegion = `${this.prefix}.KinesisRegion`;
  private readonly kinesisStreamName = `${this.prefix}.KinesisStreamName`;
  private readonly partnerRoleArn = `${this.prefix}.PartnerRoleArn`;

  constructor(
    private fb: FormBuilder,
    private config: ConfigStateService,
    private settingService: ConfigurationSettingsService,
    private toastr: ToasterService,
    private localizationService: LocalizationService
  ) {
    this.form = this.fb.group({
      sourceName: this.fb.group({
        name: this.sourceName,
        value: [
          this.config.getSetting(this.sourceName),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      organizationId: this.fb.group({
        name: this.organizationId,
        value: [
          this.config.getSetting(this.organizationId),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      clientId: this.fb.group({
        name: this.clientId,
        value: [
          this.config.getSetting(this.clientId),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      clientSecret: this.fb.group({
        name: this.clientSecret,
        value: [
          this.config.getSetting(this.clientSecret),
          {
            validators: [StringValidator.whitespace],
          },
        ],
      }),
      region: this.fb.group({
        name: this.region,
        value: [
          this.config.getSetting(this.region),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      bucketName: this.fb.group({
        name: this.bucketName,
        value: [
          this.config.getSetting(this.bucketName),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      outputBucketName: this.fb.group({
        name: this.outputBucketName,
        value: [
          this.config.getSetting(this.outputBucketName),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      templatePath: this.fb.group({
        name: this.templatePath,
        value: [
          this.config.getSetting(this.templatePath),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }), 
      outputTemplatePath: this.fb.group({
        name: this.outputTemplatePath,
        value: [
          this.config.getSetting(this.outputTemplatePath),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      kinesisRegion: this.fb.group({
        name: this.kinesisRegion,
        value: [
          this.config.getSetting(this.kinesisRegion),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      kinesisStreamName: this.fb.group({
        name: this.kinesisStreamName,
        value: [
          this.config.getSetting(this.kinesisStreamName),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      partnerRoleArn: this.fb.group({
        name: this.partnerRoleArn,
        value: [
          this.config.getSetting(this.partnerRoleArn),
          {
            validators: [StringValidator.whitespace],
          },
        ],
      }),
    });
  }

  ngOnInit(): void {}

  onSubmitForm(eventArgs): void {
    if (!this.form.valid) {
      return;
    }

    const settings: ConfigurationSettingsDto[] = [];

    settings.push({
      settingName: this.form.get('sourceName').get('name').value,
      settingValue: this.form.get('sourceName').get('value').value,
    });

    settings.push({
      settingName: this.form.get('organizationId').get('name').value,
      settingValue: this.form.get('organizationId').get('value').value,
    });

    settings.push({
      settingName: this.form.get('clientId').get('name').value,
      settingValue: this.form.get('clientId').get('value').value,
    });

    if (this.form.get('clientSecret').get('value').value) {
      settings.push({
        settingName: this.form.get('clientSecret').get('name').value,
        settingValue: this.form.get('clientSecret').get('value').value,
      });
    }

    settings.push({
      settingName: this.form.get('region').get('name').value,
      settingValue: this.form.get('region').get('value').value,
    });

    settings.push({
      settingName: this.form.get('bucketName').get('name').value,
      settingValue: this.form.get('bucketName').get('value').value,
    });

    settings.push({
      settingName: this.form.get('outputBucketName').get('name').value,
      settingValue: this.form.get('outputBucketName').get('value').value,
    });

    settings.push({
      settingName: this.form.get('templatePath').get('name').value,
      settingValue: this.form.get('templatePath').get('value').value,
    });

    settings.push({
      settingName: this.form.get('outputTemplatePath').get('name').value,
      settingValue: this.form.get('outputTemplatePath').get('value').value,
    });

    settings.push({
      settingName: this.form.get('kinesisRegion').get('name').value,
      settingValue: this.form.get('kinesisRegion').get('value').value,
    });

    settings.push({
      settingName: this.form.get('kinesisStreamName').get('name').value,
      settingValue: this.form.get('kinesisStreamName').get('value').value,
    });

    settings.push({
      settingName: this.form.get('partnerRoleArn').get('name').value,
      settingValue: this.form.get('partnerRoleArn').get('value').value,
    });

    this.settingService.saveSetting(settings).subscribe(res => {
      this.toastr.success(
        this.localizationService.instant('AbpSettingManagement::SavedSuccessfully')
      );
    });
  }
}
